import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import HistoryIcon from "@mui/icons-material/DateRange";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import GraphIcon from "@mui/icons-material/Timeline";
import { Box, Grid, Tab, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { getDateRangeCaption, helperGetIsoDate } from "../../utils/helpers";
import { deepCopyObject } from "../../utils/helpers";
import { extractGraphDataFromArray } from "../graphing/graphUtils.js";
import SingleSymptomGraph from "../graphing/singleSymptomGraph";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniGraphFab from "../ui/omniGraphFab";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const statistical = require("statistical-js");

const SingleSymptomDialog = props => {
  const {
    open,
    symptomODM,
    dialogTitle = "Symptom Information",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "Delete",
    tabId = 0,
  } = props;

  const [editedODM, setEditedODM] = useState(symptomODM);

  const [symptomData, setSymptomData] = useState(null);

  const generateSymptomData = theSymptom => {
    if (theSymptom == null || theSymptom.timeSeries.length < 2) {
      return null;
    }

    let graphData = extractGraphDataFromArray(theSymptom.timeSeries);

    let linearInput = [];
    for (let x = 0; x < graphData.values.length; ++x) {
      linearInput.push([x, graphData.values[x]]);
    }
    //this takes an array [x,y] values
    const linearOutput = statistical.methods.linearRegression(linearInput);

    const average = statistical.methods.mean(graphData.values);

    return {
      name: theSymptom.name,
      averageSeverity: Math.round(average),
      regressionValue: linearOutput,
      graphDataComplete: graphData.values,
      graphLabels: graphData.labels,
      graphIndex: 0,
      symptomId: theSymptom._id,
    };
  };

  useEffect(() => {
    setSymptomData(generateSymptomData(symptomODM));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symptomODM]);

  const updateEditedODM = event => {
    try {
      editedODM[event.target.id] = event.target.value;
      setEditedODM(deepCopyObject(editedODM));
    } catch (error) {
      console.log(error);
    }
  };

  const [showConfirm, setShowConfirm] = useState(false);

  const [initialValue, setInitialValue] = useState(
    symptomODM.timeSeries.length > 0 ? editedODM.timeSeries[0].value : 5,
  );

  const [tabIndex, setTabIndex] = useState(tabId);

  const onCloseHandler = () => {
    props.onClose();
  };

  const onAcceptHandler = () => {
    if (editedODM.timeSeries.length > 0) {
      editedODM.timeSeries[0].value = initialValue;
    } else {
      let yesterday = helperGetIsoDate(1).substring(0, 10);
      let theNewEntry = {
        date: yesterday,
        value: initialValue,
        note: "Starting Symptom Severity",
      };
      editedODM.timeSeries.push(theNewEntry);
    }

    props.onAccept(editedODM);
  };

  const theme = useTheme();

  //graphing
  const [graphTimeRange, setGraphTimeRange] = useState(constants.graphTimeRange.long);
  const [graphStyle] = useState(constants.graphTypes.regressionGraph);

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogTabs tabIndex={tabIndex} setTabIndex={setTabIndex}>
        <Tab key={`tab0`} icon={<InfoIcon />} label={`Information`} />
        <Tab key={`tab1`} icon={<GraphIcon />} label={"History"} disabled={symptomData == null} />
      </DialogTabs>

      <DialogContent>
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Grid container>
            <Typography variant="overline" sx={{ marginTop: theme.spacing(1) }}>
              Symptom Details
            </Typography>
            <Grid container item spacing={1}>
              <Grid item xs={9}>
                <OmniTextField
                  sx={{ paddingRight: "8px" }}
                  label="Name"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"name"}
                  value={editedODM.name}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                  placeholder={"Enter Symptom Name"}
                  disabled={editedODM._id != null}
                />
              </Grid>
              <Grid time xs={3}>
                <Box sx={{ paddingRight: "8px", marginTop: "8px" }}>
                  <OmniTextField
                    key={`initialValue`}
                    name={uuid()}
                    value={initialValue}
                    type="number"
                    showNumberControls={true}
                    onFocus={event => event.target.select()}
                    label={"Starting Severity"}
                    onChange={event => {
                      let onlyNums = event.target.value.toString();
                      onlyNums = onlyNums.replace(/[^0-9]/g, "");
                      onlyNums = onlyNums.slice(0, 2);
                      onlyNums = parseInt(onlyNums);
                      if (onlyNums > 10) {
                        onlyNums = 10;
                      } else if (onlyNums < 0) {
                        onlyNums = 0;
                      }
                      setInitialValue(onlyNums);
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <OmniTextField
                  label="Description"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"description"}
                  value={editedODM.description}
                  rowCount={5}
                  multiline={true}
                  onChange={event => {
                    updateEditedODM(event);
                  }}
                  placeholder={"A Short Description"}
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogTab>
        <DialogTab selectedIndex={tabIndex} index={1}>
          {symptomData != null && (
            <Grid container>
              {/* Title */}
              <Grid container item xs={12} alignItems="baseline">
                <Grid item xs={6}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "x-large",
                      whiteSpace: "pre",
                      width: "100%",
                    }}
                  >
                    {getDateRangeCaption(symptomData.graphLabels, graphTimeRange)}
                  </Typography>
                </Grid>
                <Grid container item xs={6} md={6} justifyContent="flex-end">
                  <Box
                    sx={{
                      marginTop: "-12px",
                      marginRight: "2px",
                    }}
                  >
                    {/* All */}
                    <OmniGraphFab
                      tooltipTitle="All History"
                      selected={graphTimeRange === constants.graphTimeRange.long}
                      onClick={e => {
                        e.stopPropagation();
                        setGraphTimeRange(constants.graphTimeRange.long);
                      }}
                    >
                      <HistoryIcon />
                    </OmniGraphFab>
                    {/* 6 Months */}
                    <OmniGraphFab
                      tooltipTitle="Last 6 Months"
                      selected={graphTimeRange === constants.graphTimeRange.medium}
                      onClick={e => {
                        e.stopPropagation();
                        setGraphTimeRange(constants.graphTimeRange.medium);
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        6
                      </Typography>
                    </OmniGraphFab>
                    {/* 3 Months */}
                    <OmniGraphFab
                      tooltipTitle="Last 3 Months"
                      selected={graphTimeRange === constants.graphTimeRange.short}
                      onClick={e => {
                        e.stopPropagation();
                        setGraphTimeRange(constants.graphTimeRange.short);
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        3
                      </Typography>
                    </OmniGraphFab>
                  </Box>
                </Grid>
              </Grid>
              <SingleSymptomGraph
                sx={{
                  marginLeft: theme.spacing(2),
                  width: "100%",
                }}
                graphTitle={symptomData.graphTitle}
                rawGraphData={symptomData.graphDataComplete}
                graphLabels={symptomData.graphLabels}
                graphIndex={symptomData.graphIndex}
                graphTimeRange={graphTimeRange}
                graphStyle={graphStyle}
                averageSeverity={symptomData.averageSeverity}
              />
            </Grid>
          )}
        </DialogTab>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {editedODM._id != null && ( //prototype ID
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle=""
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                // disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this symptom?"}
          onAccept={() => {
            props.onDelete(editedODM);
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(SingleSymptomDialog);
